import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import SLABadge from "@/components/SLABadge";
import { Plus, Search, Volume2 } from "lucide-react";
import { STATUS_LABELS, STATUS_COLORS } from "@/lib/constants";

export default function CitizenHome() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/complaints/mine").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="overline text-slate-500 mb-1">Citizen portal</div>
            <h1 className="font-display font-bold text-3xl tracking-tight">Your complaints</h1>
            <p className="text-slate-600 mt-1 text-sm">Track status and view resolution timelines.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/track">
              <Button variant="outline" className="gap-2" data-testid="track-btn">
                <Search className="w-4 h-4" /> Track by reference
              </Button>
            </Link>
            <Link to="/citizen/new">
              <Button className="gap-2" data-testid="new-complaint-btn">
                <Plus className="w-4 h-4" /> File a complaint
              </Button>
            </Link>
          </div>
        </div>

        {items === null && <div className="text-muted-foreground">Loading...</div>}
        {items?.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <div className="text-slate-800 font-display text-xl font-semibold">No complaints yet</div>
              <p className="text-slate-600 mt-2 text-sm">File your first noise pollution complaint. It takes less than a minute.</p>
              <Link to="/citizen/new">
                <Button className="mt-6 gap-2" data-testid="empty-state-new-btn">
                  <Plus className="w-4 h-4" /> File a complaint
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4">
          {(items || []).map((c) => (
            <Link key={c.id} to={`/citizen/${c.id}`} data-testid={`complaint-card-${c.id}`}>
              <Card className="hover:border-slate-400 transition-colors">
                <CardContent className="py-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="overline text-slate-500">{c.reference}</span>
                        <Badge variant="outline" className={STATUS_COLORS[c.status] || ""}>
                          {STATUS_LABELS[c.status] || c.status}
                        </Badge>
                        <SLABadge
                          createdAt={c.created_at}
                          levelStartedAt={c.level_started_at}
                          currentLevel={c.current_level}
                          status={c.status}
                          escalated={c.escalated}
                        />
                      </div>
                      <div className="mt-2 font-display font-semibold text-lg tracking-tight">{c.title}</div>
                      <div className="text-sm text-slate-600 mt-1 line-clamp-1">{c.address}</div>
                      {c.decibel_reading?.peak_db > 0 && (
                        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-900 text-white text-xs font-medium" data-testid={`db-badge-${c.id}`}>
                          <Volume2 className="w-3.5 h-3.5" />
                          Peak {c.decibel_reading.peak_db} dB
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-muted-foreground">Zone</div>
                      <div className="text-sm font-medium">{c.zone_name}</div>
                      <div className="text-xs text-muted-foreground mt-2">Filed</div>
                      <div className="text-sm">{new Date(c.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}
